// @flow
// localstorageSyncMiddleware


import * as Redux                  from 'redux';
import Raven                       from '../../../../modules/raven-cdn';
import type {
  Action,
  Dispatch,
  Store,
}                                  from '../../../../modules/redux/redux.types';
import type { State }              from '../../../../store';
import type { SyncableAction }     from '../../domain/syncActions.types';
import type { ImportSyncedAction } from '../../store/actions/importSyncedAction';
import importSyncedAction          from '../../store/actions/importSyncedAction';


// -----------------------------------------------------------------------------------------------------------------
// Defaults
// -----------------------------------------------------------------------------------------------------------------

const defaults: $NonMaybeType<CreateMiddlewareOptionalArgs> = {
  localstorageKey: 'actionSync',
};


// -----------------------------------------------------------------------------------------------------------------
// Middleware
// -----------------------------------------------------------------------------------------------------------------

type SyncDecisionHandler = (action: Action<*>) => ?boolean;

type CreateMiddlewareOptionalArgs = {|
  localstorageKey?: string;
|};

type CreateMiddlewareArgs = {|
  ...CreateMiddlewareOptionalArgs,
  syncDecisionHandler: SyncDecisionHandler,
|};

type Middleware = Redux.Middleware<State, Action<*>>;
type MiddlewareAPI = Redux.MiddlewareAPI<State, Action<*>>;


/**
 * @param {SyncDecisionHandler} args.syncDecisionHandler Handler to decide whether the action should be synced.
 *                                                       Should return true if action is to be synced, false otherwise.
 *                                                       If no result is given, then an error will be… thrown in dev, or
 *                                                       logged in prod.
 * @param {string}              args.localstorageKey     The key to use when writing action sync data to local storage.
 *
 * @return {function(*=): function(*): Function}
 */
// const createActionSyncMiddleware = (args: CreateMiddlewareArgs): Middleware => {
const createActionSyncMiddleware = (args: CreateMiddlewareArgs): Redux.Middleware<*, *> => {

  const {syncDecisionHandler, localstorageKey} = {...defaults, ...args};

  return (store: MiddlewareAPI) => {

    return function (next: Dispatch<Action<*>>) {

      return function (action: ImportSyncedAction | Action<*>) {

        let isSyncedAction: boolean = false;

        // Extract synced actions
        if (importSyncedAction.toString() === action.type) {
          action         = action.payload.action;
          isSyncedAction = true;
        }

        const result = next(action);

        // If this is a sync request, no need to test whether to sync the action, so just return the result
        if (!!isSyncedAction) {
          return result;
        }

        const shouldSyncAction = syncDecisionHandler(action);

        switch (shouldSyncAction) {
          case false:
            break;

          case true:
            // We need to add a timestamp to the
            const stampedAction = timestampAction(action);
            localStorage.setItem(localstorageKey, JSON.stringify(stampedAction));
            break;

          default:
            const error = new Error(
              `Don't know how to handle '${action.type}' actions.  Please update the localstorageSyncMiddleware ` +
              `'syncDecisionHandler' to add support for this action type.`,
            );
            if (process.env.NODE_ENV !== "production") {
              throw error;
            } else {
              Raven.captureException(error);
            }
        }

        return result;
      };
    };
  };
};


// -----------------------------------------------------------------------------------------------------------------
// Supporting listener
// -----------------------------------------------------------------------------------------------------------------

type CreateStorageListenerConfigArg = void | {|
  localstorageKey?: $PropertyType<CreateMiddlewareArgs, 'localstorageKey'>,
|};

function createStorageListener(store: Store, args: CreateStorageListenerConfigArg) {

  const {localstorageKey} = {...defaults, ...(args || {})};

  window.addEventListener(
    'storage',
    (event) => {
      if (event.key !== localstorageKey) {
        return;
      }

      // the storage event tells you which value changed
      const {action} = JSON.parse(event.newValue);

      const importAction = importSyncedAction({action});

      console.log('Dispatching synced action', importAction);

      store.dispatch(importAction);
    },
  );
}


// -----------------------------------------------------------------------------------------------------------------
// Supporting functions
// -----------------------------------------------------------------------------------------------------------------

function timestampAction(action) {
  return {
    action,
    syncTime: Date.now(),
  };
}


// -----------------------------------------------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------------------------------------------

export default createActionSyncMiddleware;

export { createStorageListener };



// WEBPACK FOOTER //
// ./src/modules/actionSync/store/middleware/createActionSyncMiddleware.js